import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import {
  useForgotPasswordMutation,
  useLogin,
  useUsernameMutation,
  useVerifyTwoFactorMutation,
} from "~/pages/Auth/hooks"
import { useAuth } from "~/store/auth-context"

const _usernameSchema = z.object({
  username: standardEmail(),
})

export type LoginState =
  | "username"
  | "password"
  | "forgotPassword"
  | "resetPasswordEmailSent"
  | "magicLinkEmailSent"
  | "twoFactor"

export const useLoginFlow = () => {
  const navigate = useNavigate()
  const { authenticate, getHomeRedirectUrl } = useAuth()
  const [pageState, setPageState] = useState<LoginState>("username")
  const [email, setEmail] = useState<string>("")
  const [userId, setUserId] = useState<string>("")
  const [_, setParams] = useSearchParams()

  const {
    mutateAsync: login,
    isPending: isLoggingIn,
    error: loginError,
  } = useLogin()

  const { mutateAsync: verifyTwoFactor } = useVerifyTwoFactorMutation({
    onSuccess: (data) => {
      authenticate(data)
      navigate(getHomeRedirectUrl(data))
      setPageState("username")
    },
  })

  const {
    mutateAsync: sendResetPasswordEmail,
    isPending: isSendingResetPasswordEmail,
    error: forgotPasswordError,
  } = useForgotPasswordMutation({
    onSuccess: () => setPageState("resetPasswordEmailSent"),
  })

  const {
    mutateAsync: sendUsername,
    isPending: isSendingUsername,
    error: usernameError,
  } = useUsernameMutation({
    onSuccess(data) {
      if (data.nextStep === "password") {
        setPageState("password")
      } else if (data.nextStep === "magicLink") {
        setPageState("magicLinkEmailSent")
      }
    },
    onError() {
      setPageState("username")
      setEmail("")
    },
  })

  const handleUsernameSubmit = async ({
    username,
  }: z.infer<typeof _usernameSchema>) => {
    setEmail(username)
    setParams((prev) => {
      prev.delete("error")
      return prev
    })
    await sendUsername({ username })
  }

  const handleLogin = async (values: {
    username: string
    password: string
    recaptchaToken?: string
  }) => {
    try {
      const response = await login(values)
      if (!response.twoFactor) {
        if (!response.user) throw new Error("User not found")
        authenticate(response.user)
        navigate(getHomeRedirectUrl(response.user))
        setPageState("username")
        return
      } else {
        if (!response.userId) throw new Error("User id not found")
        setPageState("twoFactor")
        setUserId(response.userId)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleVerifyTwoFactor = async ({
    token,
    userId,
  }: {
    token: string
    userId: string
  }) => {
    try {
      await verifyTwoFactor({ token, userId })
    } catch (e) {
      console.error(e)
    }
  }

  const handleForgotPassword = async () => {
    await sendResetPasswordEmail({ email })
  }

  return {
    pageState,
    setPageState,
    email,
    userId,
    isLoggingIn,
    isSendingUsername,
    isSendingResetPasswordEmail,
    loginError,
    usernameError,
    forgotPasswordError,
    handleUsernameSubmit,
    handleLogin,
    handleVerifyTwoFactor,
    handleForgotPassword,
  }
}
